import React from 'react';
import styled from 'styled-components';

// Styled Components
const SectionWrapper = styled.section`
  padding: 100px 0;
  background-color: var(--bg-light);
`;

const SectionContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 1.5rem;
`;

const ContentGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 60px;
  align-items: center;
  
  @media (max-width: 992px) {
    grid-template-columns: 1fr;
  }
`;

const TextContent = styled.div`
  @media (max-width: 992px) {
    order: 1;
  }
`;

const SectionTitle = styled.h2`
  font-size: 2.5rem;
  color: var(--text-dark);
  margin-bottom: 1.5rem;
  
  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const Description = styled.p`
  font-size: 1.1rem;
  color: var(--text-light);
  margin-bottom: 2rem;
  
  @media (max-width: 768px) {
    font-size: 1rem;
  }
`;

const IndustryList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const IndustryItem = styled.li`
  display: flex;
  align-items: flex-start;
  margin-bottom: 1.5rem;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const IconWrapper = styled.div`
  width: 2rem;
  height: 2rem;
  background-color: var(--success);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 1rem;
  margin-top: 0.25rem;
  flex-shrink: 0;
`;

const TextWrapper = styled.div`
  flex: 1;
`;

const IndustryTitle = styled.h4`
  font-size: 1.1rem;
  font-weight: 600;
  color: var(--text-dark);
  margin-bottom: 0.5rem;
`;

const IndustryDescription = styled.p`
  font-size: 0.9rem;
  color: var(--text-light);
`;

const ImagesGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  
  @media (max-width: 992px) {
    order: 2;
    margin-top: 2rem;
  }
`;

const MobileAppImage = styled.div`
  background-color: var(--bg-white);
  border-radius: 20px;
  box-shadow: var(--shadow-lg);
  overflow: hidden;
  height: 500px;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:nth-child(2) {
    margin-top: 40px;
  }
  
  @media (max-width: 768px) {
    height: 400px;
  }
`;

// SVG Components for industry items
const CheckIcon = () => (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M13.3337 4L6.00033 11.3333L2.66699 8" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

// App mockups
const FoodAppMockup = () => (
    <svg width="200" height="400" viewBox="0 0 200 400" fill="none" xmlns="http://www.w3.org/2000/svg">
        {/* Phone Frame */}
        <rect x="10" y="10" width="180" height="380" rx="20" fill="#F8FAFC" stroke="#E2E8F0" strokeWidth="2" />

        {/* Screen */}
        <rect x="20" y="20" width="160" height="360" rx="10" fill="#FEF2F2" />

        {/* App Header */}
        <rect x="20" y="20" width="160" height="60" rx="10" fill="#10B981" />
        <circle cx="40" cy="40" r="10" fill="white" fillOpacity="0.3" />
        <rect x="60" y="35" width="80" height="10" rx="5" fill="white" />
        <circle cx="160" cy="40" r="10" fill="white" fillOpacity="0.3" />

        {/* Product Grid */}
        <rect x="30" y="90" width="65" height="80" rx="8" fill="white" />
        <rect x="105" y="90" width="65" height="80" rx="8" fill="white" />
        <rect x="30" y="180" width="65" height="80" rx="8" fill="white" />
        <rect x="105" y="180" width="65" height="80" rx="8" fill="white" />
        <rect x="30" y="270" width="65" height="80" rx="8" fill="white" />
        <rect x="105" y="270" width="65" height="80" rx="8" fill="white" />

        {/* Food Images */}
        <circle cx="62" cy="120" r="25" fill="#FCD34D" />
        <circle cx="137" cy="120" r="25" fill="#F87171" />
        <circle cx="62" cy="210" r="25" fill="#6EE7B7" />
        <circle cx="137" cy="210" r="25" fill="#A78BFA" />
        <circle cx="62" cy="300" r="25" fill="#93C5FD" />
        <circle cx="137" cy="300" r="25" fill="#FDBA74" />

        {/* Text lines */}
        <rect x="40" y="150" width="45" height="5" rx="2" fill="#6B7280" />
        <rect x="115" y="150" width="45" height="5" rx="2" fill="#6B7280" />
        <rect x="40" y="240" width="45" height="5" rx="2" fill="#6B7280" />
        <rect x="115" y="240" width="45" height="5" rx="2" fill="#6B7280" />
        <rect x="40" y="330" width="45" height="5" rx="2" fill="#6B7280" />
        <rect x="115" y="330" width="45" height="5" rx="2" fill="#6B7280" />

        {/* Navigation Bar */}
        <rect x="20" y="350" width="160" height="30" rx="10" fill="white" />
        <circle cx="50" cy="365" r="8" fill="#10B981" />
        <circle cx="100" cy="365" r="8" fill="#D1D5DB" />
        <circle cx="150" cy="365" r="8" fill="#D1D5DB" />
    </svg>
);

const FashionAppMockup = () => (
    <svg width="200" height="400" viewBox="0 0 200 400" fill="none" xmlns="http://www.w3.org/2000/svg">
        {/* Phone Frame */}
        <rect x="10" y="10" width="180" height="380" rx="20" fill="#F8FAFC" stroke="#E2E8F0" strokeWidth="2" />

        {/* Screen */}
        <rect x="20" y="20" width="160" height="360" rx="10" fill="#F5F3FF" />

        {/* App Header */}
        <rect x="20" y="20" width="160" height="60" rx="10" fill="#8B5CF6" />
        <circle cx="40" cy="40" r="10" fill="white" fillOpacity="0.3" />
        <rect x="60" y="35" width="80" height="10" rx="5" fill="white" />
        <circle cx="160" cy="40" r="10" fill="white" fillOpacity="0.3" />

        {/* Featured Product */}
        <rect x="30" y="90" width="140" height="160" rx="10" fill="white" />
        <rect x="40" y="100" width="120" height="120" rx="5" fill="#E9D5FF" />
        <rect x="40" y="230" width="100" height="10" rx="5" fill="#4B5563" />

        {/* Product Grid */}
        <rect x="30" y="260" width="65" height="90" rx="8" fill="white" />
        <rect x="105" y="260" width="65" height="90" rx="8" fill="white" />

        {/* Fashion Images */}
        <rect x="40" y="270" width="45" height="50" rx="5" fill="#DDD6FE" />
        <rect x="115" y="270" width="45" height="50" rx="5" fill="#C4B5FD" />

        {/* Text lines */}
        <rect x="40" y="330" width="45" height="5" rx="2" fill="#6B7280" />
        <rect x="115" y="330" width="45" height="5" rx="2" fill="#6B7280" />

        {/* Navigation Bar */}
        <rect x="20" y="350" width="160" height="30" rx="10" fill="white" />
        <circle cx="50" cy="365" r="8" fill="#8B5CF6" />
        <circle cx="100" cy="365" r="8" fill="#D1D5DB" />
        <circle cx="150" cy="365" r="8" fill="#D1D5DB" />
    </svg>
);

// Industries Component
const Industries = () => {
    // Industry data
    const industries = [
        {
            id: 1,
            title: "生鮮食品與有機農產品",
            description: "整合送貨排程、生鮮保存期限提醒等特殊功能"
        },
        {
            id: 2,
            title: "美妝與保養品",
            description: "支援產品成分展示、適合膚質推薦等功能"
        },
        {
            id: 3,
            title: "服飾與配件",
            description: "提供尺寸指南、搭配推薦等特色功能"
        },
        {
            id: 4,
            title: "電子產品與家電",
            description: "整合規格比較、安裝服務預約等功能"
        }
    ];

    return (
        <SectionWrapper>
            <SectionContainer>
                <ContentGrid>
                    <TextContent>
                        <SectionTitle>為不同產業量身打造</SectionTitle>
                        <Description>
                            ShopWave 可以根據不同產業的需求進行調整，無論您銷售的是什麼產品，我們都能為您打造最合適的解決方案。
                        </Description>

                        <IndustryList>
                            {industries.map(industry => (
                                <IndustryItem key={industry.id}>
                                    <IconWrapper>
                                        <CheckIcon />
                                    </IconWrapper>
                                    <TextWrapper>
                                        <IndustryTitle>{industry.title}</IndustryTitle>
                                        <IndustryDescription>{industry.description}</IndustryDescription>
                                    </TextWrapper>
                                </IndustryItem>
                            ))}
                        </IndustryList>
                    </TextContent>

                    <ImagesGrid>
                        <MobileAppImage>
                            <FoodAppMockup />
                        </MobileAppImage>
                        <MobileAppImage>
                            <FashionAppMockup />
                        </MobileAppImage>
                    </ImagesGrid>
                </ContentGrid>
            </SectionContainer>
        </SectionWrapper>
    );
};

export default Industries;